import { type AllIconKeys, useResponsiveConditional } from "@biom3/react";
import type { Action, Quest } from "@imx-tokens-ts/gems-game/domain";
import { useEffect, useState } from "react";
import { type UserAgent, detectUserAgent } from "../utils";
import { getFeaturedQuestActions } from "./featured-quest-actions";
import { getLayer3QuestActions } from "./layer3-actions";
import { getMainSideQuestActions } from "./main-side-quest-actions";
import { getOnboardingQuestActions } from "./onboarding-quest-actions";

export type PrimaryTileAction = {
  action: Action;
  label?: string;
  icon?: AllIconKeys;
  link?: URL;
};

export type SecondaryTileAction = {
  action: Action;
  icon: AllIconKeys;
  link?: URL; // link will not be present if it is a rule dialog action
};

export type TileActions = {
  primaryActions: PrimaryTileAction[];
  secondaryActions: SecondaryTileAction[];
};

// Hook to get user agent after hydration to avoid SSR mismatch
function useUserAgent(): UserAgent {
  // Always start with "Other" for SSR consistency
  const [userAgent, setUserAgent] = useState<UserAgent>("Other");

  useEffect(() => {
    // Update to actual user agent after hydration
    setUserAgent(detectUserAgent(navigator.userAgent));
  }, []);

  return userAgent;
}

export function useGetTileActions(
  quest: Quest,
  isQuestComplete: boolean,
): TileActions {
  const userAgent = useUserAgent();

  const opts = {
    isQuestComplete,
    isMediumScreenOrAbove: useResponsiveConditional("medium"),
    isLargeScreenOrAbove: useResponsiveConditional("large"),
    userAgent,
  };

  if (quest.hasAction("Layer3")) {
    return getLayer3QuestActions(quest, opts);
  }

  if (quest.isFeatured()) {
    return getFeaturedQuestActions(quest, opts);
  }

  if (quest.isOnboardingQuest()) {
    return getOnboardingQuestActions(quest, opts);
  }

  return getMainSideQuestActions(quest, opts);
}
